import {
  useDocument,
  useDocumentPreview,
  useDocumentCredentialsDecrypt,
} from '../hooks';
import { useDocumentsStore } from '../store';
import {
  X,
  Eye,
  Calendar,
  User,
  Tag,
  FolderOpen,
  HardDrive,
} from 'lucide-react';
import { PdfViewer } from './PdfViewer';
import { PasswordVerifyModal } from './PasswordVerifyModal';
import { DocumentCategoryBadge } from './DocumentCategoryBadge';
import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { formatBytes } from '../utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DocumentCredentialsResponse } from '../types';

export const DocumentPreviewModal: React.FC = () => {
  const { previewDocumentUuid, closePreviewModal } = useDocumentsStore();
  const { data: document, isLoading } = useDocument(
    previewDocumentUuid || undefined
  );
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [credentialsVisible, setCredentialsVisible] = useState(false);
  const [credentialsData, setCredentialsData] =
    useState<DocumentCredentialsResponse | null>(null);
  const { mutateAsync: decryptCredentials, isPending: isDecrypting } =
    useDocumentCredentialsDecrypt();

  const baseCredentialFields = document?.metadata.credentialFields ?? [];
  const displayedCredentialFields =
    baseCredentialFields.length > 0
      ? baseCredentialFields
      : (credentialsData?.credentialFields ?? []);

  // Pour les fichiers (PDF, images), on charge le blob pour créer un object URL
  const { previewUrl, isLoading: isLoadingPreview } = useDocumentPreview(
    document?.type === 'file' ? previewDocumentUuid || undefined : undefined
  );

  const handleRevealCredentials = useCallback(() => {
    setShowPasswordModal(true);
  }, []);

  const handlePasswordVerify = useCallback(
    async (password: string) => {
      if (!previewDocumentUuid) {
        return false;
      }
      try {
        const decrypted = await decryptCredentials({
          uuid: previewDocumentUuid,
          password,
        });
        setCredentialsData(decrypted);
        setCredentialsVisible(true);
        setShowPasswordModal(false);
        return true;
      } catch {
        return false;
      }
    },
    [decryptCredentials, previewDocumentUuid]
  );

  const handleCancelPasswordVerify = useCallback(() => {
    setShowPasswordModal(false);
  }, []);

  useEffect(() => {
    setShowPasswordModal(false);
    setCredentialsVisible(false);
    setCredentialsData(null);
  }, [previewDocumentUuid]);

  if (!previewDocumentUuid) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-neutral-900/90 backdrop-blur-sm"
        onClick={closePreviewModal}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl rounded-2xl bg-white p-6 shadow-2xl">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between border-b border-neutral-200 pb-4">
            <div className="flex-1 pr-4">
              <h3 className="text-xl font-semibold text-neutral-900">
                {document?.name || 'Chargement...'}
              </h3>
              {document?.description && (
                <p className="mt-1 text-sm text-neutral-600">
                  {document.description}
                </p>
              )}
            </div>
            <button
              onClick={closePreviewModal}
              className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Document Metadata */}
          {document && (
            <div className="mb-6 grid grid-cols-1 gap-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4 md:grid-cols-2">
              {/* Catégorie */}
              <div className="flex items-start gap-3">
                <FolderOpen className="mt-0.5 h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-xs font-medium text-neutral-500">
                    Catégorie
                  </p>
                  <div className="mt-1">
                    <DocumentCategoryBadge category={document.category} />
                  </div>
                </div>
              </div>

              {/* Taille */}
              {document.size && (
                <div className="flex items-start gap-3">
                  <HardDrive className="mt-0.5 h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-xs font-medium text-neutral-500">
                      Taille
                    </p>
                    <p className="mt-1 text-sm text-neutral-900">
                      {formatBytes(document.size)}
                    </p>
                  </div>
                </div>
              )}

              {/* Date de création */}
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-xs font-medium text-neutral-500">
                    Créé le
                  </p>
                  <p className="mt-1 text-sm text-neutral-900">
                    {format(
                      new Date(document.createdAt),
                      'dd MMMM yyyy à HH:mm',
                      {
                        locale: fr,
                      }
                    )}
                  </p>
                </div>
              </div>

              {/* Date de modification */}
              {document.updatedAt !== document.createdAt && (
                <div className="flex items-start gap-3">
                  <Calendar className="mt-0.5 h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-xs font-medium text-neutral-500">
                      Modifié le
                    </p>
                    <p className="mt-1 text-sm text-neutral-900">
                      {format(
                        new Date(document.updatedAt),
                        'dd MMMM yyyy à HH:mm',
                        {
                          locale: fr,
                        }
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Uploadé par */}
              {document.uploadedByDisplayName && (
                <div className="flex items-start gap-3">
                  <User className="mt-0.5 h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-xs font-medium text-neutral-500">
                      Uploadé par
                    </p>
                    <p className="mt-1 text-sm text-neutral-900">
                      {document.uploadedByDisplayName}
                    </p>
                  </div>
                </div>
              )}

              {/* Contexte */}
              <div className="flex items-start gap-3">
                <FolderOpen className="mt-0.5 h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-xs font-medium text-neutral-500">
                    Contexte
                  </p>
                  <p className="mt-1 text-sm text-neutral-900 capitalize">
                    {document.context === 'domain'
                      ? 'Domaine'
                      : document.context === 'folder'
                        ? 'Dossier'
                        : 'Employé'}
                  </p>
                </div>
              </div>

              {/* Tags */}
              {document.tags && document.tags.length > 0 && (
                <div className="flex items-start gap-3 md:col-span-2">
                  <Tag className="mt-0.5 h-4 w-4 text-neutral-500" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-neutral-500">Tags</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {document.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-primary-100 text-primary-700 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="relative">
            {isLoading || isLoadingPreview ? (
              <div className="flex h-96 items-center justify-center">
                <div className="text-neutral-500">Chargement...</div>
              </div>
            ) : document ? (
              <div className="max-h-[85vh] overflow-auto">
                {document.type === 'text' ? (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6">
                    <pre className="font-mono text-sm whitespace-pre-wrap text-neutral-900">
                      {document.metadata.textContent || 'Aucun contenu'}
                    </pre>
                  </div>
                ) : document.type === 'credentials' ? (
                  <div className="space-y-4">
                    {!credentialsVisible && (
                      <div className="border-primary-200 bg-primary-50 rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Eye className="text-primary-600 h-5 w-5" />
                            <div>
                              <p className="text-primary-900 font-medium">
                                Hidden credentials
                              </p>
                              <p className="text-primary-700 text-sm">
                                Confirm your password to view credentials
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={handleRevealCredentials}
                            variant="primary"
                            size="sm"
                            disabled={isDecrypting}
                          >
                            {isDecrypting ? 'Decrypting...' : 'Reveal'}
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="rounded-lg border border-neutral-200 bg-white p-4">
                      <h4 className="mb-3 text-sm font-semibold text-neutral-700">
                        Credentials
                      </h4>
                      <div className="space-y-3">
                        {displayedCredentialFields.map((field, index) => {
                          const decryptedValue =
                            credentialsData?.credentialFields?.find(
                              item => item.label === field.label
                            )?.value;

                          return (
                            <div key={`${field.label}-${index}`}>
                              <label className="text-xs font-medium text-neutral-500">
                                {field.label}
                              </label>
                              <div className="mt-1 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-900">
                                {credentialsVisible ? (
                                  <span
                                    className={
                                      field.type === 'password' ||
                                      field.sensitive
                                        ? 'font-mono'
                                        : ''
                                    }
                                  >
                                    {decryptedValue ?? field.value}
                                  </span>
                                ) : (
                                  <span className="font-mono">••••••••</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : document.mimeType?.startsWith('image/') ? (
                  previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={document.name}
                      className="w-full rounded-lg"
                    />
                  ) : (
                    <div className="flex h-96 items-center justify-center">
                      <p className="text-neutral-500">
                        Chargement de l'image...
                      </p>
                    </div>
                  )
                ) : document.mimeType === 'application/pdf' ? (
                  previewUrl ? (
                    <PdfViewer fileUrl={previewUrl} fileName={document.name} />
                  ) : (
                    <div className="flex h-96 items-center justify-center">
                      <p className="text-neutral-500">Chargement du PDF...</p>
                    </div>
                  )
                ) : (
                  <div className="flex h-96 items-center justify-center">
                    <p className="text-neutral-500">
                      Aperçu non disponible pour ce type de fichier
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-96 items-center justify-center">
                <p className="text-neutral-500">Document non trouvé</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Password Verification Modal */}
      {showPasswordModal && (
        <PasswordVerifyModal
          onConfirm={handlePasswordVerify}
          onCancel={handleCancelPasswordVerify}
        />
      )}
    </div>
  );
};
